'use strict';

/**
 * Centralized customer-facing copy for the pipeline progress tracker (Level 1 + Level 2).
 * All strings pending product review live here so wording can be revised in one place.
 * Keys are stable ids; components must never hard-code user-visible tracker text.
 *
 * IMPORTANT: none of this copy may claim that support "has been notified" — no alerting
 * guarantee exists (see docs/pizza-tracker/PIPELINE_STATUS_TRACKER_UX_INVESTIGATION.md §10).
 */

/** Stable high-level stage ids (order matters; `ready` is derived and always last). */
export const PIPELINE_PROGRESS_STAGE_IDS = Object.freeze({
	received: 'received',
	transferringData: 'transferring-data',
	preparingResults: 'preparing-results',
	ready: 'ready',
});

/** Ordered Level 1 stage ids. */
export const PIPELINE_PROGRESS_STAGE_ORDER = Object.freeze([
	PIPELINE_PROGRESS_STAGE_IDS.received,
	PIPELINE_PROGRESS_STAGE_IDS.transferringData,
	PIPELINE_PROGRESS_STAGE_IDS.preparingResults,
	PIPELINE_PROGRESS_STAGE_IDS.ready,
]);

/** Level 1 step labels. */
export const PIPELINE_PROGRESS_STAGE_LABELS = Object.freeze({
	[PIPELINE_PROGRESS_STAGE_IDS.received]: 'Received',
	[PIPELINE_PROGRESS_STAGE_IDS.transferringData]: 'Transferring data',
	[PIPELINE_PROGRESS_STAGE_IDS.preparingResults]: 'Preparing results',
	[PIPELINE_PROGRESS_STAGE_IDS.ready]: 'Ready',
});

/** Level 1 status sentence while the given stage is current (no failure/delay). */
export const PIPELINE_PROGRESS_STAGE_MESSAGES = Object.freeze({
	[PIPELINE_PROGRESS_STAGE_IDS.received]: "We've received your project and publishing has started.",
	[PIPELINE_PROGRESS_STAGE_IDS.transferringData]: 'Your project data is being transferred.',
	[PIPELINE_PROGRESS_STAGE_IDS.preparingResults]: 'Your results are being prepared.',
	[PIPELINE_PROGRESS_STAGE_IDS.ready]: 'Your project is ready. All results are available.',
});

/** Tracker-level state messages (override stage messages). */
export const PIPELINE_PROGRESS_STATE_MESSAGES = Object.freeze({
	ready: 'Your project is ready. All results are available.',
	delayed: "Processing is taking longer than usual. No action is needed — we're on it.",
	failed: "Something went wrong while processing your project. Contact support if you'd like an update.",
	unavailable: "Progress tracking isn't available for this project.",
});

/**
 * "{n} of {m} sample sets complete" suffix appended to the current-stage message when counts exist.
 * @param {number} completed
 * @param {number} total
 */
export function pipelineProgressCountSuffix(completed, total) {
	return ` — ${completed} of ${total} sample sets complete.`;
}

/**
 * Sub-step labels keyed by graph node id. Used to bind pizza-tracker stages
 * to graph nodes for highlighting; node `description` strings remain on the graph.
 */
export const PIPELINE_PROGRESS_SUB_STEP_LABELS = Object.freeze({
	'v7-project-poller': 'Publishing run started',
	'v7-project-available': 'Project files transferred',
	'v7-sample-set-available': 'Sample sets transferred',
	'v7-product-sample-set-portalapi': 'Results stored',
	'v7-product-sample-set-biovisservice': 'Visualizations prepared',
	'v7-product-sample-set-pathwayexplorerapi': 'Pathway data prepared',
});

/**
 * Core (sample-set grain) customer stages. Supported only by tracker-boundary facts:
 * ProductService deliverable construction → PortalApi PSSA ingestion → derived Ready.
 * Labels pending product sign-off; keep all Core copy here.
 */
export const CORE_PROGRESS_STAGE_IDS = Object.freeze({
	preparingResults: 'core-preparing-results',
	publishing: 'core-publishing',
	ready: 'core-ready',
});

export const CORE_PROGRESS_STAGE_ORDER = Object.freeze([
	CORE_PROGRESS_STAGE_IDS.preparingResults,
	CORE_PROGRESS_STAGE_IDS.publishing,
	CORE_PROGRESS_STAGE_IDS.ready,
]);

export const CORE_PROGRESS_STAGE_LABELS = Object.freeze({
	[CORE_PROGRESS_STAGE_IDS.preparingResults]: 'Preparing results',
	[CORE_PROGRESS_STAGE_IDS.publishing]: 'Publishing',
	[CORE_PROGRESS_STAGE_IDS.ready]: 'Ready',
});

/**
 * Core sample-set graph node ids used to bind pizza-tracker stages for highlighting.
 * Plain language only — never raw service or message names.
 */
export const CORE_PROGRESS_SUB_STEP_LABELS = Object.freeze({
	'core-stats-completed': 'Statistics results built',
	'core-portal-pssa': 'Results stored in the portal',
	'core-pathway': 'Pathway data prepared',
	'core-heatmap': 'Heatmap prepared',
	'core-biovis': 'Visualizations prepared',
});

/** DiscoveryPanels Level-2 sub-step labels (graph node ids). */
export const DISCOVERY_PROGRESS_SUB_STEP_LABELS = Object.freeze({
	'dp-stats-completed': 'Statistics results built',
	'dp-portal-pssa': 'Results stored in the portal',
	'dp-discovery-panels': 'Discovery panel data prepared',
	'dp-smartpanel': 'SmartPanel data prepared',
	'dp-smartpanel-viz': 'Visualizations prepared',
});

/** Kits customer stages: ingest (Received) then DataNorm publication. */
export const KITS_PROGRESS_STAGE_IDS = Object.freeze({
	received: 'kits-received',
	preparingResults: CORE_PROGRESS_STAGE_IDS.preparingResults,
	publishing: CORE_PROGRESS_STAGE_IDS.publishing,
	ready: CORE_PROGRESS_STAGE_IDS.ready,
});

export const KITS_PROGRESS_STAGE_ORDER = Object.freeze([
	KITS_PROGRESS_STAGE_IDS.received,
	KITS_PROGRESS_STAGE_IDS.preparingResults,
	KITS_PROGRESS_STAGE_IDS.publishing,
	KITS_PROGRESS_STAGE_IDS.ready,
]);

export const KITS_PROGRESS_STAGE_LABELS = Object.freeze({
	[KITS_PROGRESS_STAGE_IDS.received]: 'Received',
	[KITS_PROGRESS_STAGE_IDS.preparingResults]: 'Preparing results',
	[KITS_PROGRESS_STAGE_IDS.publishing]: 'Publishing',
	[KITS_PROGRESS_STAGE_IDS.ready]: 'Ready',
});

/** Kits Level-2 sub-step labels (ManifestAvailable forks to catalog upsert and to DataNorm / PSSA). */
export const KITS_PROGRESS_SUB_STEP_LABELS = Object.freeze({
	'kits-manifest-available': 'Project created from the lab manifest',
	'kits-product-project-available': 'Project registered for publication',
	'kits-portal-project-available': 'Project available in the portal',
	'kits-datanorm-completed': 'Normalized results built',
	'kits-portal-pssa': 'Results stored in the portal',
	'kits-pathway': 'Pathway data prepared',
	'kits-biovis': 'Visualizations prepared',
});

export const MERGED_PROGRESS_STAGE_IDS = Object.freeze({
	received: 'merged-received',
	publishing: 'merged-publishing',
	ready: 'merged-ready',
});

export const MERGED_PROGRESS_STAGE_ORDER = Object.freeze([
	MERGED_PROGRESS_STAGE_IDS.received,
	MERGED_PROGRESS_STAGE_IDS.publishing,
	MERGED_PROGRESS_STAGE_IDS.ready,
]);

export const MERGED_PROGRESS_STAGE_LABELS = Object.freeze({
	[MERGED_PROGRESS_STAGE_IDS.received]: 'Received',
	[MERGED_PROGRESS_STAGE_IDS.publishing]: 'Publishing',
	[MERGED_PROGRESS_STAGE_IDS.ready]: 'Ready',
});

export const MERGED_PROGRESS_SUB_STEP_LABELS = Object.freeze({
	'merged-merge-projects': 'Merged project created',
	'merged-project-available': 'Project available in the portal',
});

/** Core tracker copy (sample-set grain + project roll-up). */
export const CORE_PROGRESS_COPY = Object.freeze({
	panelTitle: 'Publishing progress',
	sampleSetListLabel: 'Sample sets',
	summaryReady: (n) => `${n} ready`,
	summaryInProgress: (n) => `${n} publishing`,
	summaryFailed: (n) => `${n} need attention`,
	allReady: 'All current sample sets are ready.',
	statePreparing: 'Results for this sample set are being prepared.',
	statePublishing: 'Results for this sample set are being published.',
	stateReady: 'Results for this sample set are ready.',
	stateFailed: "Something went wrong while publishing this sample set. Contact support if you'd like an update.",
	stateDelayed: "Publishing is taking longer than usual. No action is needed — we're on it.",
	// Republication: previous results stay accessible while the new flow runs.
	updating: 'Updated results are being published. Current results remain available.',
	unavailable: "Progress tracking isn't available for this project.",
	emptyActivity: 'No current publication activity.',
});

/** Discovery panel sample-set tracker copy (mirrors Core grain). */
export const DISCOVERY_PROGRESS_COPY = Object.freeze({
	panelTitle: 'Discovery panel progress',
	sampleSetListLabel: 'Sample sets',
	summaryReady: (n) => `${n} ready`,
	summaryInProgress: (n) => `${n} publishing`,
	summaryFailed: (n) => `${n} need attention`,
	allReady: 'All current sample sets are ready.',
	statePreparing: 'Results for this sample set are being prepared.',
	statePublishing: 'Results for this sample set are being published.',
	stateReady: 'Results for this sample set are ready.',
	stateFailed: "Something went wrong while publishing this sample set. Contact support if you'd like an update.",
	stateDelayed: "Publishing is taking longer than usual. No action is needed — we're on it.",
	updating: 'Updated results are being published. Current results remain available.',
	unavailable: "Progress tracking isn't available for this project.",
	emptyActivity: 'No current publication activity.',
});

/** Kits sample-set tracker copy. */
export const KITS_PROGRESS_COPY = Object.freeze({
	panelTitle: 'Kits publishing progress',
	sampleSetListLabel: 'Sample sets',
	summaryReady: (n) => `${n} ready`,
	summaryInProgress: (n) => `${n} publishing`,
	summaryFailed: (n) => `${n} need attention`,
	allReady: 'All current sample sets are ready.',
	stateReceived: 'Your project has been created. Normalized results will appear here once analysis completes.',
	statePreparing: 'Results for this sample set are being prepared.',
	statePublishing: 'Results for this sample set are being published.',
	stateReady: 'Results for this sample set are ready.',
	stateFailed: "Something went wrong while publishing this sample set. Contact support if you'd like an update.",
	stateDelayed: "Publishing is taking longer than usual. No action is needed — we're on it.",
	updating: 'Updated results are being published. Current results remain available.',
	unavailable: "Progress tracking isn't available for this project.",
	emptyActivity: 'No current publication activity.',
});

/**
 * Merged projects track MergeProjects then PortalApi ProjectAvailable.
 * Customer stages are Received → Publishing → derived Ready.
 */
export const MERGED_PROGRESS_COPY = Object.freeze({
	panelTitle: 'Publishing progress',
	receivedStepLabel: 'Received',
	publishingStepLabel: 'Publishing',
	readyStepLabel: 'Ready',
	received: 'Your merged project is being created.',
	publishing: 'Your merged project has been created and is being published to the portal.',
	ready: 'Your merged project is ready.',
	failed: "Something went wrong while creating your merged project. Contact support if you'd like an update.",
	unavailable: 'No merge activity recorded for this project yet.',
});

export const MERGED_PROGRESS_STAGE_MESSAGES = Object.freeze({
	[MERGED_PROGRESS_STAGE_IDS.received]: MERGED_PROGRESS_COPY.received,
	[MERGED_PROGRESS_STAGE_IDS.publishing]: MERGED_PROGRESS_COPY.publishing,
	[MERGED_PROGRESS_STAGE_IDS.ready]: MERGED_PROGRESS_COPY.ready,
});

/** Panel title + misc UI copy. */
export const PIPELINE_PROGRESS_UI_COPY = Object.freeze({
	panelTitle: 'Publishing progress',
	viewDetails: 'View details',
	hideDetails: 'Hide details',
});
